/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {useEffect, useState} from 'react';

export type WorkspaceRect = {top: number, left: number, width: number, height: number};

/**
 * The `useWorkspaceRect` hook tracks the viewport rectangle of a workspace
 * element, kept current as the window or the workspace itself resizes. It
 * lets an overlay (e.g. a modal and its scrim) cover only that element
 * rather than the whole viewport.
 *
 * State tracked is the element's `top`, `left`, `width` and `height`, or
 * `null` while `active` is `false` or the element doesn't exist. It is
 * measured synchronously when `workspaceId` or `active` changes, so the
 * overlay has its position before the first paint. Afterwards it changes
 * on a window `resize` or a `ResizeObserver` notification, throttled to at
 * most one re-measure per animation frame, and only when a measured value
 * differs from the previous one.
 *
 * ```typescript
 *   import useWorkspaceRect from '@ion/dls/src/hooks/useWorkspaceRect';
 *   ...
 *
 *   const SomeOverlay = ({open}: SomeOverlayProps) => {
 *     const rect  = useWorkspaceRect('layout-root', open);
 *     const style = rect ? {top: rect.top, left: rect.left} : undefined;
 *     ...
 *     return (
 *       <Dialog open={open} style={style} />
 *     );
 *   };
 * ```
 *
 * @param workspaceId The id of the element whose rectangle is tracked.
 * @param active      Tracks the element only while `true`.
 * @returns           The element's viewport rectangle, otherwise `null`.
 */
export const useWorkspaceRect = (workspaceId: string, active: boolean): WorkspaceRect | null => {
  const [rect, setRect] = useState<WorkspaceRect | null>(null);

  useEffect(() => {
    const workspace = active ? document.getElementById(workspaceId) : null;
    const measure   = function() {
      const {top, left, width, height} = workspace.getBoundingClientRect();
      // keeps the previous rectangle when nothing moved, skipping a render ...
      setRect(function(prev) {
        const unchanged = prev?.top === top && prev.left === left && prev.width === width && prev.height === height;
        return unchanged ? prev : {top, left, width, height};
      });
    };
    // throttles the resize/ResizeObserver-driven re-measures to at most one
    // per animation frame - `pendingFrame` is the already-scheduled frame's
    // id, so a burst of events before it runs schedules only the one ...
    let pendingFrame: number | null = null;
    const throttledMeasure = function() {
      if (pendingFrame === null) {
        pendingFrame = requestAnimationFrame(function() {
          pendingFrame = null;
          measure();
        });
      }
    };
    const observer = workspace && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(throttledMeasure) : null;
    if (workspace) {
      // the initial measurement stays synchronous, so the modal has its
      // position before the first paint rather than one frame later ...
      measure();
      observer?.observe(workspace);
      window.addEventListener('resize', throttledMeasure);
    }
    return function() {
      observer?.disconnect();
      window.removeEventListener('resize', throttledMeasure);
      if (pendingFrame !== null) {
        cancelAnimationFrame(pendingFrame);
      }
    };
  }, [workspaceId, active]);

  return rect;
};

export default useWorkspaceRect;
